/**
 * MIS RETOS: los que mandé y los que me mandaron, en una sola lista.
 *
 * UNA LISTA Y NO DOS. «Mis retos» es lo que la persona tiene pendiente con
 * otra gente, y de qué lado salió la propuesta es un detalle de cada fila, no
 * una división de la pantalla. Partirla en «mandados» y «recibidos» obliga a
 * mirar dos lugares para saber qué falta hacer.
 *
 * CADA FILA TIENE UNA SOLA COSA PARA HACER, Y ES DISTINTA SEGÚN QUIÉN MIRA:
 *
 *  · Me lo mandaron y está pendiente → aceptar o rechazar.
 *  · Lo mandé y está pendiente → cancelarlo, o esperar.
 *  · Está aceptado → anotar quién ganó. Cualquiera de los dos.
 *  · Ya se jugó → el marcador, y los puntos si contaba para el ranking.
 *
 * EL MARCADOR SIEMPRE EN EL ORDEN DE QUIEN MIRA: lo suyo primero, en la
 * pantalla y al anotar. Lo da vuelta el servidor, una vez, porque es el único
 * que sabe con certeza quién está preguntando.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { formatScore, type Challenge, type MatchScore } from '@tennisup/contracts';

import { Alert } from './Alert';
import { Avatar } from './Avatar';
// LAS MISMAS DOS FUNCIONES QUE EL EDITOR DE TORNEO: «lo escrito a marcador» y
// «¿hay un set con un solo número?». Copiarlas acá daría dos formas de leer
// los mismos campos, y la que se quede vieja acepta lo que la otra rechaza.
import { ScoreEditor } from './ScoreEditor';
import { useAuth } from '../auth/AuthContext';
import { api, assetUrl } from '../lib/api';
import { errorMessage } from '../lib/errorMessage';
import { formatDate } from '../pages/TournamentsPage';

export function MyChallenges(): JSX.Element {
  const { t } = useTranslation();
  const [retos, setRetos] = useState<Challenge[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    try {
      const lista = await api.myChallenges();
      /*
       * `Array.isArray` Y NO `?? []`. Es la cuarta vez en este proyecto que
       * una pantalla se cae porque el cuerpo de la respuesta no era lo que el
       * tipo prometía —un `{}`, un `null`, un cuerpo vacío—: un tipo es una
       * promesa sobre el contrato, no una garantía sobre el cable.
       */
      setRetos(Array.isArray(lista) ? lista : []);
    } catch (caught) {
      setError(errorMessage(t, caught));
    }
  }, [t]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  if (error !== null) return <Alert kind="error">{error}</Alert>;
  if (retos === null) return <p className="help">{t('common.loading')}</p>;
  if (retos.length === 0) return <p className="help">{t('challenge.none')}</p>;

  return (
    <ul className="challenge-list">
      {retos.map((reto) => (
        <ChallengeRow key={reto.id} reto={reto} onChange={cargar} />
      ))}
    </ul>
  );
}

function ChallengeRow({
  reto,
  onChange,
}: {
  reto: Challenge;
  onChange: () => Promise<void>;
}): JSX.Element {
  const { t } = useTranslation();
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [anotando, setAnotando] = useState(false);

  async function hacer(accion: () => Promise<unknown>): Promise<void> {
    setError(null);
    setOcupado(true);
    try {
      await accion();
      await onChange();
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setOcupado(false);
    }
  }

  return (
    <li className="challenge-row">
      <div className="challenge-who">
        <Avatar url={assetUrl(reto.them.avatarPath)} name={reto.them.displayName} size={40} />
        <div>
          <Link to={`/jugadores/${reto.them.userId}`}>{reto.them.displayName}</Link>
          <p className="help">
            {[
              // Quién propuso: es lo primero que se quiere saber al mirar la
              // fila, y sin decirlo las dos se ven iguales.
              reto.mine ? t('challenge.iSent') : t('challenge.theySent'),
              reto.kind === 'RANKING'
                ? t('challenge.rankedIn', { club: reto.communityName ?? '' })
                : t('challenge.friendlyTag'),
            ].join(' · ')}
          </p>
        </div>
      </div>

      {error !== null && <Alert kind="error">{error}</Alert>}

      <Plazo reto={reto} />

      {reto.status === 'PLAYED' && !anotando ? (
        <>
          <Resultado reto={reto} />
          <Confirmar reto={reto} onDone={() => void onChange()} />

          {/*
            CORREGIR UN MARCADOR YA ANOTADO.

            `canFixScore` lo dice el SERVIDOR con la misma regla que aplica al
            guardar —los dos que jugaron por un día, la plataforma siempre—.
            Deducirlo acá sería un botón que se inventa un permiso, y eso es un
            403 con forma de botón.
          */}
          {(reto.canFixScore || reto.canClearScore) && (
            <div className="button-row">
              {reto.canFixScore && (
                <button type="button" className="link" onClick={() => setAnotando(true)}>
                  {t('challenge.fixScore')}
                </button>
              )}
              {/*
                BORRAR EL MARCADOR: el partido vuelve a estar sin anotar.

                `canClearScore` lo dice el servidor, igual que `canFixScore` y
                por lo mismo. En un reto solo puede la plataforma.

                PIDE CONFIRMACIÓN, y es de las pocas cosas de la aplicación que
                lo hacen: borrar un marcador le saca puntos a dos personas que
                no están mirando esta pantalla. Un toque sin querer no puede
                hacer eso.
              */}
              {reto.canClearScore && <BorrarMarcador reto={reto} onDone={onChange} />}
            </div>
          )}
        </>
      ) : anotando ? (
        <AnotarResultado
          reto={reto}
          /*
            EL AVISO DE QUE YA ESTABA ANOTADO.

            Javier: «si trata de caer encima mencionar que ha sido reportado y
            si esta de acuerdo volver a editarlo». Antes, cuando los dos
            anotaban al salir de la cancha, el segundo recibía un error —«ya lo
            anotaron»—. Desde que se puede corregir ese error desapareció, y sin
            este aviso el segundo marcador pisaría al primero EN SILENCIO.

            El editor se abre CON EL MARCADOR QUE YA ESTÁ, así que quien llega
            segundo ve lo que anotó el otro antes de escribir nada: deja de ser
            una carrera a ciegas y pasa a ser una decisión.
          */
          yaAnotado={reto.status === 'PLAYED'}
          onCancel={() => setAnotando(false)}
          onSave={async (score, winnerUserId, superTieBreak) => {
            await hacer(() =>
              api.setChallengeResult(reto.id, { winnerUserId, score, superTieBreak }),
            );
          }}
          /*
           * LA FOTO SE SUBE SOLA, sin esperar a Guardar. El reto ya existe
           * —está aceptado— así que hay dónde colgarla, y es lo mismo que
           * hace un partido de cuadro. Guardarla junto con el marcador
           * obligaría a mandar el archivo otra vez si el marcador rebota.
           */
          onPhoto={async (file) => {
            await hacer(() =>
              file === null
                ? api.removeChallengePhoto(reto.id)
                : api.setChallengePhoto(reto.id, file),
            );
          }}
          /* Y LA FRASE DESPUÉS DEL MARCADOR: hasta que el reto no está
             jugado, el servidor no deja comentarlo — y tiene razón. */
          onComment={async (body) => {
            await hacer(() => api.addChallengeComment(reto.id, body));
          }}
        />
      ) : (
        <Acciones
          reto={reto}
          ocupado={ocupado}
          onAnotar={() => setAnotando(true)}
          onAnswer={(accept) => void hacer(() => api.answerChallenge(reto.id, accept))}
          onCancel={() => void hacer(() => api.cancelChallenge(reto.id))}
        />
      )}
    </li>
  );
}

/**
 * CANCELAR UN RETO YA ACEPTADO, en dos pasos.
 *
 * EN DOS PASOS Y NO CON UN `confirm()` DEL NAVEGADOR: el aviso tiene que decir
 * QUÉ pasa —que al otro le llega— y eso no cabe en un diálogo del sistema que
 * además no se puede traducir.
 */
function CancelarAceptado({
  ocupado,
  onCancel,
}: {
  ocupado: boolean;
  onCancel: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [confirmando, setConfirmando] = useState(false);

  if (!confirmando) {
    return (
      <button
        type="button"
        className="link danger"
        disabled={ocupado}
        onClick={() => setConfirmando(true)}
      >
        {t('challenge.cancelPlayed')}
      </button>
    );
  }

  return (
    <>
      <p className="help">{t('challenge.cancelPlayedConfirm')}</p>
      <button type="button" className="link danger" disabled={ocupado} onClick={onCancel}>
        {t('challenge.cancelPlayedYes')}
      </button>
      <button type="button" className="link" onClick={() => setConfirmando(false)}>
        {t('common.back')}
      </button>
    </>
  );
}

/** Lo que se puede hacer con un reto que todavía no se jugó. */
function Acciones({
  reto,
  ocupado,
  onAnswer,
  onCancel,
  onAnotar,
}: {
  reto: Challenge;
  ocupado: boolean;
  onAnswer: (accept: boolean) => void;
  onCancel: () => void;
  onAnotar: () => void;
}): JSX.Element {
  const { t } = useTranslation();

  if (reto.status === 'ACCEPTED') {
    return (
      <div className="challenge-actions">
        <button type="button" className="secondary" disabled={ocupado} onClick={onAnotar}>
          {t('challenge.record')}
        </button>
        {/*
          CANCELAR UN RETO QUE LOS DOS ACEPTARON.

          Javier: «si ambos aceptan el reto pero no se concreta tener la opción
          de cancelarlo». Antes, un partido que no se jugaba —llovió, uno se
          lesionó, no coincidieron— quedaba vivo para siempre en la lista de
          los dos, sin forma de sacarlo salvo inventar un marcador.

          LO PUEDEN LOS DOS, no solo quien lo mandó: el partido pasó a ser de
          los dos, y si solo pudiera uno, el otro queda esperando a que limpie.

          PIDE CONFIRMACIÓN, al revés que cancelar uno sin contestar: acá el
          otro YA dijo que sí y le va a llegar un aviso. Un toque sin querer no
          puede desarmar algo que dos personas acordaron.
        */}
        <CancelarAceptado ocupado={ocupado} onCancel={onCancel} />
      </div>
    );
  }

  if (reto.status !== 'PENDING') {
    // Rechazado o cancelado: no hay nada que hacer, solo saber en qué quedó.
    return <p className="help">{t(`challenge.status.${reto.status}`)}</p>;
  }

  if (reto.mine) {
    return (
      <div className="challenge-actions">
        <p className="help">{t('challenge.waiting')}</p>
        <button type="button" className="link danger" disabled={ocupado} onClick={onCancel}>
          {t('challenge.cancel')}
        </button>
      </div>
    );
  }

  return (
    <div className="challenge-actions">
      <button
        type="button"
        className="secondary"
        disabled={ocupado}
        onClick={() => onAnswer(true)}
      >
        {t('challenge.accept')}
      </button>
      <button type="button" className="link danger" disabled={ocupado} onClick={() => onAnswer(false)}>
        {t('challenge.decline')}
      </button>
    </div>
  );
}

/**
 * EL PLAZO PARA JUGARLO, o el aviso de que se pasó.
 *
 * Javier: «una vez aceptado el reto poner fecha límite de una semana para
 * reportar el marcador, si no solo se pone una leyenda no cumplieron con el
 * partido en el tiempo dado». Resuelve algo que se ve en la lista: dos
 * aceptan, no se ponen de acuerdo, y el reto queda meses sin que nadie sepa
 * si va a pasar.
 *
 * NO BLOQUEA NADA, y así lo pidió: «solo se pone una leyenda». El partido
 * pudo haberse jugado tarde, y borrar un resultado real por llegar tarde
 * sería peor que la demora. Lo que hace la leyenda es que alguien escriba por
 * WhatsApp.
 *
 * LA FECHA VIENE DEL SERVIDOR. Una pantalla que calcula sola el plazo es una
 * pantalla que un día muestra otro plazo que el que se aplica.
 */
function Plazo({ reto }: { reto: Challenge }): JSX.Element | null {
  const { t } = useTranslation();
  if (reto.reportDeadline === null) return null;

  /*
   * SE COMPARAN DOS FECHAS CIVILES COMO TEXTO, y es a propósito: `YYYY-MM-DD`
   * ordena igual alfabéticamente que cronológicamente. Convertirlas a `Date`
   * las volvería instantes con zona horaria, y un reto que vence hoy se
   * leería como vencido en Costa Rica seis horas antes de que lo esté.
   *
   * `sv-SE` no es un gusto sueco: es el único idioma que `toLocaleDateString`
   * formatea como `YYYY-MM-DD`, y lo hace en la zona de quien mira, que es la
   * que decide qué día es hoy para esa persona.
   */
  const hoy = new Date().toLocaleDateString('sv-SE');
  const tarde = hoy > reto.reportDeadline;

  return tarde ? (
    <p className="challenge-late">{t('challenge.late')}</p>
  ) : (
    <p className="help">{t('challenge.deadline', { date: formatDate(reto.reportDeadline) })}</p>
  );
}

/** Cómo terminó, ya jugado. */
function Resultado({ reto }: { reto: Challenge }): JSX.Element {
  const { t } = useTranslation();
  return (
    <p className="challenge-result">
      {/* TRES FINALES Y NO DOS: un amistoso puede quedar empatado. Con el
          ternario de antes, un empate se leía como derrota. */}
      <strong>
        {reto.result === 'DRAW'
          ? t('challenge.drew')
          : reto.result === 'WON'
            ? t('challenge.won')
            : t('challenge.lost')}
      </strong>{' '}
      <span>{formatScore(reto.score)}</span>
      {/* LOS PUNTOS SOLO SI SON DE RANKING Y SI DEJARON ALGO. Un «0 puntos»
          al lado de un amistoso invita a preguntar por qué no dio ninguno,
          cuando la respuesta es que nunca iba a dar. */}
      {reto.kind === 'RANKING' && reto.points !== null && reto.points > 0 && (
        <span className="challenge-points">{t('challenge.points', { count: reto.points })}</span>
      )}
      {/*
        SIN CONFIRMAR SE DICE, y se dice acá al lado del marcador y no en un
        cartel aparte: es una condición DE ESTE resultado. Mientras siga así
        el partido no suma, y quien lo anotó tiene que poder ver por qué sus
        puntos no se movieron.
      */}
      {!reto.confirmed && <span className="tag tag-warning">{t('friendly.unconfirmed')}</span>}
    </p>
  );
}

/**
 * EL BOTÓN DE CONFIRMAR, para el rival.
 *
 * SOLO LE SALE A QUIEN TIENE QUE CONFIRMAR —`waitingMyConfirmation` lo
 * calcula el servidor, que es el único que sabe quién anotó— y desaparece
 * apenas confirma. Un botón que le aparece también a quien lo escribió sería
 * ofrecerle darse la razón a sí mismo.
 */
/**
 * BORRAR EL MARCADOR: el partido vuelve a estar sin anotar.
 *
 * Javier: «solo queda como si no hubiera ocurrido el partido y podamos volver a
 * poner el marcador». No borra el reto: borra su resultado.
 *
 * PIDE CONFIRMACIÓN EN DOS PASOS, y es de las pocas cosas de la aplicación que
 * lo hacen. La razón: esto le saca puntos a dos personas que no están mirando
 * esta pantalla y que no se van a enterar hasta que miren el ranking. Un toque
 * sin querer no puede tener ese efecto.
 *
 * Y LA CONFIRMACIÓN ES UN SEGUNDO BOTÓN, no un `confirm()` del navegador: el
 * diálogo nativo no se puede traducir, se ve distinto en cada teléfono, y en
 * algunos navegadores viene con «no volver a preguntar» — que es exactamente lo
 * que no se quiere acá.
 */
function BorrarMarcador({ reto, onDone }: { reto: Challenge; onDone: () => void }): JSX.Element {
  const { t } = useTranslation();
  const [confirmando, setConfirmando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function borrar(): Promise<void> {
    setEnviando(true);
    setError(null);
    try {
      await api.clearChallengeResult(reto.id);
      onDone();
    } catch (caught) {
      setError(errorMessage(t, caught));
      // SE VUELVE AL PRIMER PASO SI FALLA: dejar la confirmación abierta
      // invita a tocar de nuevo el mismo botón que acaba de fallar.
      setConfirmando(false);
    } finally {
      // `finally` Y NO SOLO EN EL `catch`: por el camino de éxito también hay
      // que apagarlo. Es el mismo defecto que dejó «Enviando…» para siempre en
      // el editor de marcadores.
      setEnviando(false);
    }
  }

  if (!confirmando) {
    return (
      <>
        {error !== null && <Alert kind="error">{error}</Alert>}
        <button type="button" className="link danger" onClick={() => setConfirmando(true)}>
          {t('challenge.clearScore')}
        </button>
      </>
    );
  }

  return (
    <>
      <p className="help">{t('challenge.clearScoreConfirm')}</p>
      <button type="button" className="link danger" disabled={enviando} onClick={() => void borrar()}>
        {enviando ? t('common.sending') : t('challenge.clearScoreYes')}
      </button>
      <button type="button" className="link" disabled={enviando} onClick={() => setConfirmando(false)}>
        {t('common.cancel')}
      </button>
    </>
  );
}

function Confirmar({ reto, onDone }: { reto: Challenge; onDone: () => void }): JSX.Element | null {
  const { t } = useTranslation();
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!reto.waitingMyConfirmation) return null;

  async function confirmar(): Promise<void> {
    setEnviando(true);
    setError(null);
    try {
      await api.confirmFriendly(reto.id);
      onDone();
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="challenge-confirm">
      <p className="help">{t('friendly.confirmHelp', { name: reto.them.displayName })}</p>
      {error !== null && <Alert kind="error">{error}</Alert>}
      <p className="button-row">
        <button type="button" disabled={enviando} onClick={() => void confirmar()}>
          {t('friendly.confirm')}
        </button>
      </p>
    </div>
  );
}

/**
 * ANOTAR QUIÉN GANÓ, con el editor de siempre.
 *
 * ACÁ HABÍA UN SEGUNDO EDITOR DE MARCADOR, y por eso la pantalla que mandó
 * Javier se veía así: dos campos al ancho de la tarjeta, uno debajo del otro,
 * sin nombres y sin estilo. Usaba las clases `.score-set` y
 * `.score-set-label`, que no existían en ninguna hoja del proyecto.
 *
 * Ahora usa `ScoreEditor`, el mismo de los partidos de torneo, que ya tenía
 * lo que él pedía: los dos nombres como filas y los sets como columnas. Dos
 * editores del mismo marcador es lo que hizo que uno quedara sin estilar
 * durante meses sin que nadie lo notara.
 *
 * LO QUE EL RETO LE PIDE DISTINTO son dos interruptores: puede empatar si es
 * amistoso, y no tiene «no se presentó» —el servidor todavía no lo acepta, y
 * un interruptor que lleva a un error no es un interruptor—.
 *
 * EL GANADOR SALE DEL MARCADOR y viene calculado del editor, que ya lo sabe
 * para dibujar «gana Fulano». Volver a deducirlo acá serían dos cuentas del
 * mismo hecho.
 */
function AnotarResultado({
  reto,
  yaAnotado = false,
  onSave,
  onCancel,
  onPhoto,
  onComment,
}: {
  reto: Challenge;
  /** Si ya tenía marcador: se avisa antes de dejar escribir encima. */
  yaAnotado?: boolean;
  /** `winnerUserId` es `null` si empataron. Solo pasa en los amistosos. */
  onSave: (
    score: MatchScore,
    winnerUserId: string | null,
    superTieBreak: boolean,
  ) => Promise<void>;
  onCancel: () => void;
  onPhoto: (file: File | null) => Promise<void>;
  onComment: (body: string) => Promise<void>;
}): JSX.Element {
  const { t } = useTranslation();
  const { session } = useAuth();
  const yo = session?.user.id ?? '';

  return (
    <>
    {yaAnotado && <Alert kind="info">{t('challenge.alreadyReported')}</Alert>}
    <ScoreEditor
      /* `home` es SIEMPRE quien mira: así se escribe y así se muestra. */
      home={{
        id: yo,
        name: session?.user.displayName ?? '',
        avatarPath: session?.user.avatarPath ?? null,
      }}
      away={{ id: reto.them.userId, name: reto.them.displayName, avatarPath: reto.them.avatarPath }}
      score={reto.score}
      noShowId={null}
      photoPath={reto.photoPath}
      /* La clave del campo de la foto: única en la pantalla, que puede tener
         varios retos abiertos a la vez. */
      photoKey={reto.id}
      allowDraw={reto.kind === 'FRIENDLY'}
      allowNoShow={false}
      onCancel={onCancel}
      onPhoto={onPhoto}
      onComment={onComment}
      /* SE PREGUNTA EL SÚPER TIE BREAK: es un reto, y acá es donde los clubes
         juegan a tie break en vez de tercer set. Ver `ScoreEditor`. */
      askSuperTieBreak
      superTieBreak={reto.superTieBreak}
      onSave={(score, _noShow, winner, superTieBreak) =>
        onSave(
          score,
          winner === null ? null : winner === 'home' ? yo : reto.them.userId,
          superTieBreak,
        )
      }
    />
    </>
  );
}
